<template>
    <div class="mx-2 w-full">
        <div class="flex flex-wrap gap-3">
            <div
                v-for="(item, index) in itemsCurrent"
                :key="index"
                class="flex items-center gap-3 flex-1 min-w-48 min-h-24 border border-theme-border rounded-xl px-4 py-3 bg-theme-card shadow-md"
            >
                <div class="flex-1 min-w-0">
                    <span
                        class="text-xs font-semibold uppercase tracking-widest text-theme-base"
                    >
                        Colección
                    </span>
                    <p
                        class="text-sm font-semibold text-theme-primary truncate mt-0.5"
                    >
                        {{ item.name }}
                    </p>
                    <span class="text-xs text-theme-muted font-mono">
                        {{ item.slug }}
                    </span>
                </div>
                <div class="shrink-0">
                    <app-button
                        v-if="enabled"
                        danger
                        :disabled="isLoadingComponent"
                        icon
                        @click="deleteCollection(index)"
                    >
                        🗑️
                    </app-button>
                </div>
            </div>

            <p
                v-if="!itemsCurrent.length"
                class="w-full text-sm text-theme-muted"
            >
                {{ emptyMessage }}
            </p>

            <div
                v-if="enabled"
                class="flex items-center gap-3 flex-1 min-w-48 min-h-24 border-2 border-dashed border-theme-border rounded-xl px-4 py-3 shadow-md"
            >
                <div class="flex-1 min-w-0">
                    <span
                        class="text-xs font-semibold uppercase tracking-widest text-theme-muted"
                    >
                        Nueva colección
                    </span>
                </div>
                <button
                    type="button"
                    :disabled="isLoadingComponent"
                    class="shrink-0 flex items-center justify-center w-10 h-10 rounded-full hover:bg-theme-hover transition-colors cursor-pointer"
                    @click="openNewCollection"
                >
                    <span class="text-xl">➕</span>
                </button>
            </div>
        </div>

        <div v-if="enabled" class="flex my-4">
            <div class="w-full flex justify-center">
                <slot name="action"></slot>
            </div>
        </div>

        <div>
            <app-dialog v-model="selectCollection" size="lg">
                <template #header>
                    <div
                        class="flex items-center justify-between gap-4 px-6 py-4 border-b border-theme-border w-full"
                    >
                        <div>
                            <h3 class="text-lg font-bold text-theme-base">
                                🏷️ Selecciona una colección
                            </h3>
                        </div>
                        <div class="flex items-center gap-2">
                            <app-button
                                :disabled="!selectedItems.length"
                                @click="pushNewCollection"
                            >
                                ✔️ Seleccionar ({{ selectedItems.length }})
                            </app-button>
                            <app-button
                                icon
                                circle
                                color="brand-purple"
                                @click="loadCollections(true)"
                            >
                                <reload-icon class="h-5 w-5 text-white" />
                            </app-button>
                        </div>
                    </div>
                </template>
                <div class="px-4 pt-3 pb-1">
                    <input
                        v-model="filterText"
                        type="text"
                        placeholder="Buscar colecciones..."
                        class="w-full px-3 py-2 rounded-lg border border-theme-border text-sm text-theme-base outline-none focus:border-stack-primary transition-colors"
                    />
                </div>
                <div
                    v-if="filteredItems.length"
                    class="grid grid-col-1 sm:grid-cols-2 gap-2 px-4 py-3"
                >
                    <button
                        v-for="(item, index) in filteredItems"
                        :key="index"
                        class="w-60 text-left px-4 py-3 rounded-lg border-2 bg-theme-card shadow-sm hover:shadow-md hover:bg-theme-hover transition-all cursor-pointer"
                        :class="
                            selectedItems.includes(item.slug)
                                ? 'border-theme-primary ring-theme-primary ring-offset-1'
                                : 'border-theme-border'
                        "
                        @click="selectionCollection(item.slug)"
                    >
                        <div class="flex items-center justify-between gap-2">
                            <h3
                                class="font-medium"
                                :class="
                                    selectedItems.includes(item.slug)
                                        ? 'text-theme-primary'
                                        : 'text-theme-base'
                                "
                            >
                                {{ item.name }}
                            </h3>
                        </div>
                        <p
                            class="text-sm mt-1"
                            :class="
                                selectedItems.includes(item.slug)
                                    ? 'text-theme-primary opacity-70'
                                    : 'text-theme-muted'
                            "
                        >
                            {{ item.summary | cut(40) }}
                        </p>
                    </button>
                </div>
                <div v-else class="px-4 py-3">
                    <span class="font-bold text-md">
                        Nuestro sistema no encuentra resultados 😢
                    </span>
                </div>
                <div
                    v-if="pagesNumber > currentPage"
                    class="flex justify-center mt-4 px-4 pb-3"
                >
                    <app-button @click="loadMoreCollections">
                        Cargar más
                    </app-button>
                </div>
            </app-dialog>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import ReloadIcon from "@/components/icons/ReloadIcon.vue";
import AppDialog from "@/components/utils/AppDialog.vue";

export default {
    name: "CollectionsCurrentManage",
    components: { AppDialog, ReloadIcon },
    model: {
        prop: "itemsCurrent",
        event: "change"
    },
    props: {
        itemsCurrent: {
            default: () => [],
            type: Array,
            require: true
        },
        enabled: {
            default: () => true,
            type: Boolean,
            required: false
        },
        emptyMessage: {
            default: () => "Todavía no hay colecciones destacadas.",
            type: String,
            required: false
        },
        defaultFilters: {
            default: () => ({
                active: true
            }),
            type: Object,
            required: false
        }
    },
    emits: ["change"],
    data: () => ({
        selectCollection: false,
        selectedItems: [],
        items: [],
        isLoadingComponent: false,
        currentPage: 1,
        pagesNumber: 0,
        filterText: ""
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        filteredItems() {
            return this.items.filter(
                (item) =>
                    item.name
                        .toLowerCase()
                        .includes(this.filterText.toLowerCase()) ||
                    item.summary
                        .toLowerCase()
                        .includes(this.filterText.toLowerCase())
            );
        }
    },
    watch: {
        itemsCurrent(value) {
            this.setInput(value);
        }
    },
    mounted() {
        this.setInput(this.itemsCurrent);
    },
    methods: {
        ...mapActions("collections", ["listCollectionsPaginated"]),
        ...mapActions("products", ["updateProductsCollections"]),
        async openNewCollection() {
            this.selectCollection = true;
            await this.loadCollections();
        },
        async loadMoreCollections() {
            this.currentPage = this.currentPage + 1;
            await this.loadCollections();
        },
        async loadCollections(required = true) {
            this.isLoadingComponent = true;
            try {
                const { items, pagination } =
                    await this.listCollectionsPaginated({
                        page: this.currentPage,
                        required,
                        ...this.defaultFilters
                    });
                this.items = items;
                if (pagination) {
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.isLoadingComponent = false;
            } catch (error) {
                this.isLoadingComponent = false;
                this.selectCollection = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        selectionCollection(slug) {
            const idx = this.selectedItems.indexOf(slug);
            if (idx === -1) {
                this.selectedItems.push(slug);
            } else {
                this.selectedItems.splice(idx, 1);
            }
        },
        pushNewCollection() {
            let collectionsAux = this.items.filter((i) =>
                this.selectedItems.includes(i.slug)
            );
            if (collectionsAux.length) {
                const collectionsResult = JSON.parse(
                    JSON.stringify(this.itemsCurrent)
                );

                collectionsAux = [...collectionsResult, ...collectionsAux];

                collectionsAux = collectionsAux.filter(
                    (value, index, self) =>
                        self.findIndex((item) => item.slug === value.slug) ===
                        index
                );
                this.selectCollection = false;
                this.$emit("change", collectionsAux);
            }
        },
        deleteCollection(fromIndex) {
            let collectionsResult = [...this.itemsCurrent];
            collectionsResult.splice(fromIndex, 1);
            this.$emit("change", collectionsResult);
        },
        setInput(value) {
            this.selectedItems = value.map((item) => item.slug);
        }
    }
};
</script>

<style scoped>
.vs-card__group .vs-card__group-cards .vs-card-content {
    min-width: 15rem;
    margin-left: 20px;
}
</style>
